/**
 * 📋 SDLC 6.3.1 UNIFIED SPECIFICATION STANDARD FILE HEADER
 * ===============================================================================
 * FILE: frontend/src/pages/CompensationMasterData/jobFamilySchema.ts
 * PURPOSE: EntitySchema<JobFamily> wiring the generic MasterDataCrudPage to the
 *          new /api/v2/compensation/job-families CRUD + Excel API (BR-COMP-002
 *          §8 R1/R2). Mirrors the EmployeeManagement/employeeSchema.ts precedent.
 * SDLC FRAMEWORK VERSION: 6.3.1 (7-Pillar + 3-Section + Long-Running Agent Protocol)
 * DATE: 2026-07-12
 * COMPLIANCE: SDLC 6.3.1 + BR-PLAT-001-MASTER-DATA-CRUD-IMPORT-EXPORT-STANDARD
 * PILLAR: P1 (Stage 06 - BUILD) + P6 (UI/UX Design)
 * SPRINT GOVERNANCE: Rule 4: Tier-3 Code Review
 *
 * @business-context
 * - Domain: Vietnamese Compensation Master Data
 * - Impact: Revenue Critical — the `multiplier` field edited on this screen is the
 *   SSOT input to the Q2 bonus formula (DD-0079). Editing it here genuinely
 *   changes bonus calculation; that is the entire point of DD-0079 landing first.
 * - Stakeholders: CFO / HR Director (TENANT_ADMIN)
 *
 * @technical-architecture
 * - Layer: Frontend Presentation Layer, Page-level config
 * - Pattern: Config-Driven UI (Strategy — schema supplies behavior)
 * - Dependencies: MasterDataCrudPage, compensationMasterData API service
 *
 * @cross-references
 * - BR: docs/01-planning/01-requirements/08-compensation/BR-COMP-002-job-family-paygrade-management.md (§8.1 form spec)
 * - Backend API: backend/app/api/v2/endpoints/compensation_master_data.py
 * - Decision (JSONB -> textarea-as-JSON): docs/02-design/18-adrs/06-decisions/DD-0080-master-data-framework-gap-resolutions.md §3
 * - Precedent: frontend/src/pages/EmployeeManagement/employeeSchema.ts
 * - Route: frontend/src/App.tsx (/tenant-admin/compensation/job-families)
 */

import type { EntitySchema, FieldDef } from '@/components/master-data/types';
import type { ColumnDef } from '@/components/dashboard/DataTable';
import {
  JOB_FAMILY_API_BASE,
  compensationMasterDataClient,
  type JobFamily,
} from '@/services/api/compensationMasterData';

/**
 * JSONB fields (`name_localized`, `assignment_rules`) are edited as JSON text in
 * a textarea, per DD-0080 §3/§4: the platform deliberately did NOT build a
 * generic nested-collection editor, and textarea-as-JSON is the sanctioned
 * representation for narrow JSONB fields like these. The same blob is carried as
 * a JSON string in the Excel cell (DD-0080 §5), so both surfaces agree.
 */
const validateOptionalJson = (value: unknown): string | undefined => {
  if (value === '' || value === null || value === undefined) return undefined;
  if (typeof value === 'object') return undefined; // already parsed (edit mode)
  try {
    JSON.parse(String(value));
    return undefined;
  } catch {
    return 'JSON không hợp lệ (VD: {"vi": "Lãnh đạo", "en": "Leadership"})';
  }
};

const validateMultiplier = (value: unknown): string | undefined => {
  if (value === '' || value === null || value === undefined) return undefined;
  // Mirrors CheckConstraint("multiplier > 0", name="positive_multiplier")
  return Number(value) > 0 ? undefined : 'Hệ số nhân phải > 0';
};

const validateGradeMin = (value: unknown): string | undefined => {
  if (value === '' || value === null || value === undefined) return undefined;
  // Mirrors the >= 1 half of CheckConstraint("grade_min >= 1 AND grade_min <= grade_max").
  // The cross-field half (grade_min <= grade_max) cannot be expressed in
  // FieldDef.validate (single-value signature — a real framework limitation,
  // see DD-0080 follow-ups); the backend enforces it and returns a 422 with a
  // Vietnamese message, which the page surfaces.
  return Number(value) >= 1 ? undefined : 'Bậc nhỏ nhất phải >= 1';
};

const columns: ColumnDef<JobFamily>[] = [
  { key: 'code', label: 'Mã', sortable: true, width: '120px' },
  { key: 'name', label: 'Tên nhóm vai trò', sortable: true },
  {
    key: 'multiplier',
    label: 'Hệ số nhân (Q2)',
    align: 'center',
    render: (value) => (value !== null && value !== undefined ? Number(value).toFixed(2) : '—'),
  },
  {
    key: 'grade_min',
    label: 'Bậc từ',
    align: 'center',
    render: (value) => (value ?? '—') as string,
  },
  {
    key: 'grade_max',
    label: 'Bậc đến',
    align: 'center',
    render: (value) => (value ?? '—') as string,
  },
  { key: 'description', label: 'Mô tả', render: (value) => (value as string) || '—' },
];

const formFields: FieldDef[] = [
  {
    key: 'code',
    label: 'Mã Job Family',
    type: 'text',
    required: true,
    placeholder: 'JF-LEAD',
    helpText:
      'Khóa tự nhiên (duy nhất trong tổ chức). Không thể sửa sau khi tạo — backend bỏ qua mọi thay đổi mã.',
  },
  { key: 'name', label: 'Tên hiển thị', type: 'text', required: true, placeholder: 'Leadership' },
  {
    key: 'multiplier',
    label: 'Hệ số nhân Q2 bonus',
    type: 'number',
    required: true,
    defaultValue: 1.0,
    helpText:
      'Nhân trực tiếp vào công thức Q2 bonus (DD-0079: cột DB này là nguồn sự thật duy nhất). Sửa ở đây là thay đổi tiền thưởng thật.',
    validate: validateMultiplier,
  },
  {
    key: 'grade_min',
    label: 'Bậc nhỏ nhất',
    type: 'number',
    required: true,
    defaultValue: 1,
    validate: validateGradeMin,
  },
  { key: 'grade_max', label: 'Bậc lớn nhất', type: 'number', required: true, defaultValue: 13 },
  { key: 'description', label: 'Mô tả', type: 'textarea', rows: 3 },
  {
    key: 'name_localized',
    label: 'Tên đa ngôn ngữ (JSON)',
    type: 'textarea',
    rows: 3,
    placeholder: '{"vi": "Lãnh đạo", "en": "Leadership"}',
    helpText: 'JSONB — nhập JSON hợp lệ (DD-0080 §3).',
    validate: validateOptionalJson,
  },
  {
    key: 'assignment_rules',
    label: 'Quy tắc tự động gán (JSON)',
    type: 'textarea',
    rows: 4,
    placeholder: '{"keywords": ["quản lý", "giám đốc"]}',
    helpText: 'JSONB — nhập JSON hợp lệ.',
    validate: validateOptionalJson,
  },
];

/**
 * RBAC (BR-COMP-002 §8 R5 as RESOLVED by the @cpo ratification review 2026-07-09):
 * TENANT_ADMIN tier ONLY — Branch Manager gets NO access, not even read. The BR's
 * original draft defaulted to allowing Branch Manager read via
 * check_manager_or_above; the @cpo explicitly overruled that (compensation
 * multipliers and pay bands are sensitive). The backend enforces the same on
 * every route including GET/export; these client-side permissions merely hide
 * controls, they are not the security boundary.
 */
export const jobFamilySchema: EntitySchema<JobFamily> = {
  entityKey: 'job-families',
  apiBasePath: JOB_FAMILY_API_BASE,
  entityLabel: 'Job Family',
  httpClient: compensationMasterDataClient, // /api/v2/* — not the default v1 client
  columns,
  formFields,
  permissions: {
    create: ['TENANT_OWNER', 'TENANT_ADMIN'],
    edit: ['TENANT_OWNER', 'TENANT_ADMIN'],
    delete: ['TENANT_OWNER', 'TENANT_ADMIN'],
  },
  importExport: {
    enabled: true,
    // Round-trippable column set == BR-COMP-002 §8.1 == the backend's
    // JOB_FAMILY_IMPORT_SCHEMA ColumnSpec list (AC-COMP-002-13).
    templateColumns: [
      'code',
      'name',
      'name_localized',
      'description',
      'grade_min',
      'grade_max',
      'multiplier',
      'assignment_rules',
    ],
    exportFileName: 'job-families.xlsx',
  },
};

export default jobFamilySchema;
